import React, { useEffect, useState } from 'react';
import { Field, Form } from 'react-final-form';
import axios from 'axios';
import * as Routes from 'modules/routes';
import IGlossaryTerm from '@/components/interfaces/GlossaryTerm';
import Spinner from '@/components/common/Spinner';
import { SelectField } from '../../../common/Forms';
import styles from '../Games.module.scss';
import useLessonGamesStore from '../../../../stores/useLessonGamesStore';
import showToast from '../../../common/Toast';

interface VocabularyGameFormProps {
  allVocab: IGlossaryTerm[];
  edit: boolean;
  gameId?: number;
  lessonId?: number;
  modalManager: ModalManager;
  programId?: number;
}

interface FormValues {
  gameTemplateId: number;
  glossaryTermIds: number[];
}

const VocabularyGameForm = ({
  allVocab, edit, gameId, lessonId: propLessonId, modalManager, programId: propProgramId,
}: VocabularyGameFormProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [linkedVocab, setLinkedVocab] = useState<IGlossaryTerm[]>([]);
  const [loading, setLoading] = useState(true);

  const {
    gameTemplates,
    glossaryTerms: storeTerms,
    lessonId: storeLessonId,
    programId: storeProgramId,
  } = useLessonGamesStore();

  // Use store value if available, else prop value
  const glossaryTerms = (storeTerms.length ? storeTerms : allVocab).filter(term => term.kind !== 'Academic');
  const lessonId = storeLessonId ?? propLessonId;
  const programId = storeProgramId ?? propProgramId;
  const initialTermIds = edit && linkedVocab ? linkedVocab.map(v => v.id) : [];

  const onSubmit = async ({ gameTemplateId, glossaryTermIds }: FormValues) => {
    const path = edit ? Routes.plato_api_add_vocab_options_games_path()
      : Routes.plato_api_create_vocab_game_games_path();
    setIsSubmitting(true);

    axios
      .post(path, {
        game: {
          game_id: gameId, game_template_id: gameTemplateId, glossary_term_ids: glossaryTermIds, lesson_id: lessonId,
        },
        program_id: programId,
      })
      .then((res) => {
        window.location.assign(Routes.edit_admin_game_path(res.data.game.id, {
          lesson_id: lessonId, program_id: programId,
        }));
      })
      .catch(() => {
        showToast('Something went wrong. Please try again later.', { msgType: 'error', position: 'top-right' });
        setIsSubmitting(false);
      });
  };

  useEffect(() => {
    if (!edit) return;

    axios.get(Routes.plato_api_get_glossary_terms_games_path({ params: { id: gameId } }))
      .then((res) => {
        setLinkedVocab(res.data.data.map(term => ({
          id: term.id,
          kind: term.kind,
          sectionTitle: term.sectionTitle,
          term: term.term,
        })));
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  if (loading && edit) return <Spinner />;

  return (
    <Form
      initialValues={{ glossaryTermIds: initialTermIds }}
      onSubmit={onSubmit}
      render={({ handleSubmit, values, form: { change } }) => (
        <form className={styles.form} onSubmit={handleSubmit}>
          <div>
            {!edit && (
              <div className={styles.gameTemplateSelectWrapper}>
                <SelectField
                  label="Type"
                  name="gameTemplateId"
                  options={gameTemplates.map(({ id, name }) => ({
                    label: name, value: id,
                  }))}
                />
              </div>
            )}
            <p className={styles.vocabLabel}>Vocabulary Terms</p>
            <label className={styles.checkboxWrapper}>
              <input
                checked={values.glossaryTermIds.length === glossaryTerms.length}
                type="checkbox"
                onChange={(e) => {
                  if (e.target.checked) {
                    change('glossaryTermIds', glossaryTerms.map(({ id }) => id));
                  } else {
                    change('glossaryTermIds', []);
                  }
                }}
              />
              Select All
            </label>
            <div className={styles.checkboxesWrapper}>
              {glossaryTerms.map(({ id, sectionTitle, term }) => (
                <div key={id} className={styles.checkboxWrapper}>
                  <label className="custom-control-label">
                    <Field
                      component="input"
                      disabled={edit && initialTermIds.includes(id)}
                      name="glossaryTermIds"
                      type="checkbox"
                      value={id}
                    />
                    {term}
                    {' '}
                    <span className={styles.sectionTitle}>({sectionTitle})</span>
                  </label>
                </div>
              ))}
            </div>
          </div>
          <div className={styles.footer}>
            <button className="btn btn--link-purple" onClick={modalManager.close} type="button">Cancel</button>
            <button
              className="btn btn--purple"
              disabled={!(edit || values.gameTemplateId) || values.glossaryTermIds.length === 0 || isSubmitting}
              type="submit"
            >
              {isSubmitting ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      )}
    />
  );
};

export default VocabularyGameForm;
